/**
 * hk_os · <NextTaskCard>
 * ────────────────────────────────────────────────────────────────────
 * Core HK primitive: the "next room" view. Attendants never browse room
 * lists — this card is the single source of "what to do next". After
 * completion the parent replaces the roomNumber prop; nothing else moves.
 *
 * Props:
 *   roomNumber   — required
 *   taskTypeKey  — voice snippet key (e.g. VOICE_ROOM_DIRTY)
 *   floorLabel   — optional
 *   badges       — [{label, tone}] (same as TaskProgressCard)
 *   ctaKey       — voice snippet key for the button (default VOICE_START_TASK)
 *   onStart      — click handler
 *   loading      — true while a task is being loaded
 *   empty        — true if no next task
 *   emptyKey     — snippet key for empty-state voice ("nothing next")
 *   testid
 */
import React from "react";
import { PlayCircle, Loader2, PartyPopper } from "lucide-react";
import { VoicePlayer } from "./VoicePlayer";
import { VoiceInstructionBar } from "./VoiceInstructionBar";
import { TaskCompletionButton } from "./TaskCompletionButton";

const BADGE_TONE = {
  vip:      "bg-amber-100 text-amber-900 border-amber-300",
  priority: "bg-red-100 text-red-900 border-red-300",
  info:     "bg-sky-100 text-sky-900 border-sky-300",
  warning:  "bg-orange-100 text-orange-900 border-orange-300",
};

export function NextTaskCard({
  roomNumber,
  taskTypeKey,
  floorLabel,
  badges = [],
  ctaKey = "VOICE_START_TASK",
  onStart,
  loading = false,
  empty = false,
  emptyKey = "VOICE_SHIFT_ENDED",
  testid,
}) {
  if (loading) {
    return (
      <div
        data-testid={testid ? `${testid}-loading` : "next-task-loading"}
        className="rounded-3xl border-2 border-slate-200 bg-slate-50 min-h-[360px] p-8 flex flex-col items-center justify-center text-slate-500"
      >
        <Loader2 className="w-10 h-10 animate-spin mb-3" />
        <div className="text-[16px] font-semibold">Loading next task…</div>
      </div>
    );
  }

  if (empty) {
    return (
      <section
        data-testid={testid ? `${testid}-empty` : "next-task-empty"}
        className="rounded-3xl border-2 border-emerald-200 bg-emerald-50 min-h-[360px] p-8 flex flex-col items-center justify-center text-emerald-800"
      >
        <PartyPopper className="w-12 h-12 mb-3" strokeWidth={2.2} />
        <div className="text-[24px] font-bold mb-3">All done</div>
        <VoicePlayer voiceKey={emptyKey} size="lg" showText
                       testid={testid ? `${testid}-empty-voice` : "next-task-empty-voice"} />
      </section>
    );
  }

  return (
    <section
      data-testid={testid || "next-task-card"}
      className="rounded-3xl border-2 border-slate-200 bg-white overflow-hidden"
    >
      <VoiceInstructionBar
        voiceKey={taskTypeKey}
        variant="hero"
        autoPlay
        testid={testid ? `${testid}-instruction` : "next-task-instruction"}
      />
      <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-4">
        <div className="text-[13px] font-semibold uppercase tracking-widest text-slate-500">
          Next Room
        </div>
        <div
          data-testid={testid ? `${testid}-number` : "next-task-number"}
          className="text-[112px] sm:text-[144px] font-black leading-none text-slate-900 select-none"
        >
          {roomNumber ?? "—"}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {floorLabel && (
            <span className="text-[14px] font-semibold text-slate-500">
              {floorLabel}
            </span>
          )}
          {badges.map((b, i) => (
            <span
              key={`${b.label}-${i}`}
              data-testid={testid ? `${testid}-badge-${b.tone}` : `next-task-badge-${b.tone}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold ${
                BADGE_TONE[b.tone] || BADGE_TONE.info
              }`}
            >
              {b.label}
            </span>
          ))}
        </div>
        <TaskCompletionButton
          voiceKey={ctaKey}
          icon={PlayCircle}
          tone="primary"
          onClick={onStart}
          testid={testid ? `${testid}-start` : "next-task-start"}
        />
      </div>
    </section>
  );
}

export default NextTaskCard;
